import React, { useMemo, useState } from "react";
import { Crosshair, Loader2, Search, Video } from "lucide-react";
import { Input } from "./ui/input";

export const SkycamCard = ({ station, selected, onSelect, badge, testId }) => (
  <button
    type="button"
    onClick={() => onSelect(station)}
    data-testid={testId || `skycam-card-${station.id}`}
    className={`sd-panel flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors ${selected ? "border-cyan-400/50 bg-cyan-500/[0.06]" : "hover:bg-white/[0.04]"}`}
  >
    <img src={station.poster} alt="" loading="lazy" className="h-12 w-20 shrink-0 rounded-md object-cover" />
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-semibold text-slate-100">{station.name}</div>
      <div className="mono mt-0.5 flex items-center gap-2 truncate text-[10px] uppercase tracking-widest text-slate-500">
        <span className={station.online === false ? "text-slate-500" : "text-emerald-300"}>{station.online === false ? "Offline" : "Live"}</span>
        {station.distance_km != null && <span>{station.distance_km < 100 ? `${station.distance_km} km` : `${Math.round(station.distance_km)} km`}</span>}
        {badge && <span className="text-cyan-400">{badge}</span>}
        {station.location && <span className="truncate">{station.location}</span>}
      </div>
    </div>
  </button>
);

// Searchable list of every HazCam station, sorted by distance when the visitor shares location.
export const SkycamDirectory = ({ stations, selected, onSelect, locate, locating, userLocation, loading }) => {
  const [query, setQuery] = useState("");
  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = needle ? stations.filter((station) => `${station.name} ${station.id} ${station.network || ""}`.toLowerCase().includes(needle)) : stations;
    return list.slice(0, 60);
  }, [stations, query]);

  return (
    <div data-testid="skycam-directory" className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input data-testid="skycam-search-input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search city, state or station…" className="h-10 border-white/10 bg-white/[0.03] pl-9 text-slate-100 placeholder:text-slate-500" />
        </div>
        <button type="button" data-testid="skycam-nearest-button" onClick={locate} title="Sort by distance from me" className={`mono flex h-10 shrink-0 items-center gap-1.5 border px-3 text-[10px] font-bold uppercase tracking-widest transition-colors ${userLocation ? "border-cyan-400/50 bg-cyan-500/15 text-cyan-100" : "border-white/15 bg-white/[0.03] text-slate-200 hover:bg-white/[0.08]"}`}>
          {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />} Near me
        </button>
      </div>
      <div className="mono mt-2 text-[10px] uppercase tracking-widest text-slate-500" data-testid="skycam-directory-count">
        {loading ? "Loading HazCam network…" : `${stations.length} cameras${userLocation ? " · nearest first" : ""}${query ? ` · ${results.length} match` : ""}`}
      </div>
      <div className="mt-2 grid min-h-0 flex-1 content-start gap-2 overflow-y-auto pr-1 sm:grid-cols-2 xl:grid-cols-1">
        {!loading && results.length === 0 && (
          <div className="sd-panel rounded-xl p-6 text-center text-sm text-slate-400" data-testid="skycam-directory-empty"><Video className="mx-auto mb-2 h-6 w-6 text-slate-600" />No cameras match “{query}”.</div>
        )}
        {results.map((station) => <SkycamCard key={station.id} station={station} selected={selected?.id === station.id} onSelect={onSelect} />)}
      </div>
    </div>
  );
};

export default SkycamDirectory;
